import { useEffect, useRef, useState } from "react";

import { useApolloClient } from "@apollo/client";

import { DataTable } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { classNames } from "primereact/utils";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { Dropdown } from "primereact/dropdown";

/** contexts */
import { useSettings } from "../../context/settings";
import { useUser } from "../../context/users";

/** QUERIES MUTATIONS */
import UPDATE_PASSENGER from "../../provider/Passengers/mutation";
import { PASSENGERS } from "../../provider/Passengers/query";
import { UPDATE_USER } from "../../provider/Users/mutation";
import { USERS_PENDING } from "../../provider/Users/query";

/** utils */
import { getGraphsqErros } from "../../utilities/functions";

const Crud = () => {
  const settings = useSettings();
  const apollo = useApolloClient();

  const user = useUser();
  const franchise_id = user?.getUser()?.franchise?.id;
  const acting_region_id = user?.getUser()?.actingRegion?.id;

  const toast = useRef<any>(null);
  const dt = useRef<any>(null);

  const [records, setRecords] = useState<any[]>([]);
  const [franchises, setFranchises] = useState<iFranchise[]>([]);
  const [regions, setRegions] = useState<iRegion[]>([]);
  const [usersPending, setUsersPending] = useState<{data: iUser[]}>();
  const [record, setRecord] = useState<iPassenger>();
  const [franchise, setFranchise] = useState<any>(null);
  const [region, setRegion] = useState<any>(null);
  const [userId, setUserId] = useState<any>(null);

  const [loading, setLoading] = useState<boolean>(false);
  const [load, setLoad] = useState(true);
  const [aproving, setAproving] = useState<boolean>(false);
  const [recordDialog, setRecordDialog] = useState<boolean>(false);
  const [deleteRecordDialog, setDeleteRecordDialog] = useState<boolean>(false);
  const [aproveUserDialog, setAproveUserDialog] = useState<boolean>(false);

  const [submitted, setSubmitted] = useState<boolean>(false);
  const [globalFilter, setGlobalFilter] = useState<any>(null);

  let emptyRecord: iPassenger = {
    name: "",
  };

  const loadRecords = (cleanRecord: boolean = false) => {
    setLoading(true);

    const filter: any = {};

    if (franchise_id) filter.franchise_id = Number(franchise_id);

    if (franchise?.code) filter.franchise_id = Number(franchise?.code);

    apollo
      .query({
        query: PASSENGERS,
        variables: {
          filter,
        },
        fetchPolicy: "no-cache",
      })
      .then(response => {
        const data = response.data.passengers;

        setFranchises(response.data.franchises);
        setRecords(data);
        setLoading(false);
        setLoad(false);
      })
      .catch(error => {
        console.log('PASSENGERS error: ', JSON.stringify(error));
        setRecords([]);
        settings?.setErrorMessage(getGraphsqErros(error));
        settings?.setVisibleError(true);
        setLoading(false);
        setLoad(false);
      });
  };

  const loadPendingUsers = (cleanRecord: boolean = false) => {
    const filter: any = {
      acting_region_id: null,
      is_callcenter: true,
    };

    if (franchise_id) filter.franchise_id = Number(franchise_id);

    if (franchise?.code) filter.franchise_id = Number(franchise?.code);

    apollo
      .query({
        query: USERS_PENDING,
        variables: filter,
      })
      .then(response => {
        const data = response.data;
        setRegions(data.regions);
        setUsersPending({data: data.users});
        setAproving(false);
      })
      .catch(error => {
        console.log('USERS error: ', JSON.stringify(error));
        settings?.setErrorMessage(getGraphsqErros(error));
        settings?.setVisibleError(true);
        setAproving(false);
      });
  };

  const hideDialog = () => {
    setSubmitted(false);
    setRecordDialog(false);
    setAproveUserDialog(false);
  };

  const hideDeleteRecordDialog = () => {
    setDeleteRecordDialog(false);
  };

  const saveRecord = () => {
    setSubmitted(true);

    if (!record?.name) {
      return;
    }

    const _record: any = { ...record };

    _record.franchise_id = franchise?.code ? parseInt(franchise.code) : undefined;

    if (franchise_id) {
      _record.franchise_id = Number(franchise_id);
    }

    delete _record.__typename;
    delete _record.franchise;
    delete _record.phone;
    delete _record.image_path;
    delete _record.cpf;
    delete _record.evaluation;

    setLoading(true);
    const mutation = UPDATE_PASSENGER;

    if (record?.id) _record.id = parseInt(`${_record.id}`);

    apollo
      .mutate({
        mutation,
        variables: {
          data: {
            ..._record,
          },
        },
      })
      .then(response => {
        setLoading(false);

        toast.current.show({ severity: "success", summary: "Sucesso", detail: "Registro salvo com sucesso.", life: 3000 });

        loadRecords();
        setRecordDialog(false);
        setRecord(emptyRecord);
        setFranchise(null);
      })
      .catch(error => {
        console.log('UPDATE_PASSENGER error: ', JSON.stringify(error));
        setLoading(false);
        settings.setErrorMessage(getGraphsqErros(error));
        settings.setVisibleError(true);
      });
  };

  const editRecord = (record: iPassenger) => {
    const _record: iPassenger = { ...record };

    setFranchise({ code: _record.franchise?.id, name: _record.franchise?.name });
    setRecord({ ..._record });
    setRecordDialog(true);
  };

  const confirmDeleteRecord = (record: any) => {
    setRecord(record);
    setDeleteRecordDialog(true);
  };

  const deleteRecord = () => {
    apollo
      .mutate({
        mutation: UPDATE_PASSENGER,
        variables: {
          data: {
            id: parseInt(`${record?.id}`),
            is_deleted: true,
          },
        },
      })
      .then(response => {
        let _records = records.filter((val: any) => val.id !== record?.id);
        setRecords(_records);
        setDeleteRecordDialog(false);
        setRecord(emptyRecord);
        toast.current.show({ severity: "success", summary: "Sucesso", detail: "Registro deletado", life: 3000 });
      })
      .catch(error => {
        setLoading(false);
        settings.setErrorMessage(getGraphsqErros(error));
        settings.setVisibleError(true);
      });
  };

  const aproveUser = (id: string | number, actingRegionId: any, franchiseId?: string | number) => {
    setLoading(true);
    const data: any = {
      id: Number(id),
      acting_region_id: Number(actingRegionId.code | actingRegionId)
    }

    if (franchiseId) data.franchise_id = franchiseId;

    apollo
      .mutate({
        mutation: UPDATE_USER,
        variables: {
          data,
        },
      })
      .then(response => {
        toast.current.show({ severity: "success", summary: "Sucesso", detail: "Usuário aprovado!", life: 3000 });

        loadPendingUsers();

        setTimeout(() => {
          window.location.reload();
        }, 5000)

        setAproveUserDialog(false);
        setLoading(false);
        setFranchise(null);
        setRegion(null);
        setUserId(null);
      })
      .catch(error => {
        console.log('UPDATE_USER error: ', JSON.stringify(error));
        setLoading(false);
        settings.setErrorMessage(getGraphsqErros(error));
        settings.setVisibleError(true);
      });
  }

  const onInputChange = (e: any, name: string) => {
    let val = (e.target && e.target.value) || "";

    let _record: any = { ...record };
    _record[name] = val;

    setRecord(_record);
  };

  const _renderUserPendings = () => {
    if (!usersPending || usersPending.data.length === 0) return;

    return usersPending.data.map((user: iUser) => (
      <>
        <div className="field">
          <span>
            {user.name}
          </span>
        </div>
        <div className="field">
          <span>
            {user.phone}
          </span>
        </div>
        <div className="field">
          <Button
            label={aproving ? 'Aguarde...' : 'Aprovar'}
            disabled={aproving}
            className="mr-2 mb-2"
            onClick={() => {
              if (!acting_region_id) {
                setUserId(user.id);
                setAproveUserDialog(true);
              } else {
                setAproving(true);
                aproveUser(user.id || 0, acting_region_id);
              }
            }}
          />
        </div>
      </>
    ))
  }

  const codeBodyTemplate = (rowData: iPassenger) => {
    return (
      <>
        <span className="p-column-title">ID</span>
        {rowData.id}
      </>
    );
  };

  const nameBodyTemplate = (rowData: iPassenger) => {
    return (
      <>
        <span className="p-column-title">Nome</span>
        {rowData.name}
      </>
    );
  };

  const phoneBodyTemplate = (rowData: iPassenger) => {
    return (
      <>
        <span className="p-column-title">Telefone</span>
        {rowData.phone}
      </>
    );
  };

  const evaluationBodyTemplate = (rowData: iPassenger) => {
    const star = rowData.evaluation?.star ? parseInt(rowData.evaluation.star) : 0;
    const list: any[] = [];
    for (let index = 0; index < 5; index++) {
      list.push(star > index ? (
        <i key={`${rowData.name} ${rowData.id} ${index}`} className="pi pi-star-fill" style={{ fontSize: "1rem" }} />
      ) : (
        <i key={`${rowData.name} ${rowData.id} ${index}`} className="pi pi-star" style={{ fontSize: "1rem" }} />
      ))
    }
    return (
      <>
        <span className="p-column-title">Avaliação</span>
        {list}
        {` (${rowData.evaluation?.amount})`}
      </>
    );
  };

  const franchiseBodyTemplate = (rowData: iServices) => {
    return (
      <>
        <span className="p-column-title">Franquia</span>
        {rowData.franchise?.name}
      </>
    );
  };

  const actionBodyTemplate = (rowData: iPassenger) => {
    return (
      <div className="actions">
        <Button icon="pi pi-pencil" className="p-button-rounded p-button-info mr-2" onClick={() => editRecord(rowData)} />
        <Button icon="pi pi-trash" className="p-button-rounded p-button-danger mt-0" onClick={() => confirmDeleteRecord(rowData)} />
      </div>
    );
  };

  const header = (
    <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center">
      <h5 className="m-0">{loading ? "Carregando clientes..." : "Clientes"}</h5>
      <span className="block mt-2 md:mt-0 p-input-icon-left">
        <i className="pi pi-search" />
        <InputText className="min-w-full" type="search" onInput={e => setGlobalFilter(e.currentTarget.value)} placeholder="Procurar..." />
      </span>
    </div>
  );

  const recordDialogFooter = (
    <>
      <Button label="Cancelar" icon="pi pi-times" disabled={loading} className="p-button-text text-red-500" onClick={hideDialog} />
      <Button label={loading ? "Salvando..." : "Salvar"} disabled={loading} icon="pi pi-check" className="p-button-text" onClick={saveRecord} />
    </>
  );

  const deleteRecordDialogFooter = (
    <>
      <Button label="Não" icon="pi pi-times" className="p-button-text text-red-500" onClick={hideDeleteRecordDialog} />
      <Button label="Sim, excluir!" icon="pi pi-check" className="p-button-text" onClick={deleteRecord} />
    </>
  );

  const aproveUserDialogFooter = (
    <>
      <Button label="Cancelar" icon="pi pi-times" disabled={loading} className="p-button-text text-red-500" onClick={hideDialog} />
      <Button label={loading ? "Aprovando..." : "Aprovar"} disabled={loading || !userId || !region} icon="pi pi-check" className="p-button-text" onClick={() => aproveUser(userId, region)} />
    </>
  );

  useEffect(() => {
    loadRecords(true);
    loadPendingUsers();
  }, [franchise?.code]);

  return (
    <div className="grid crud-demo">
      <div className="col-12">
      {franchise_id ? null : (
        load ? (
          <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }} />
        ) : (
          <>
            <div className="card">
              <div className="flex flex-column md:flex-row flex-wrap">
                { !franchise_id ? (
                  <div className="flex flex-column md:flex-row flex-wrap">
                    <label htmlFor="name">Franquia</label>
                    <div className="field col-12 md:col-12">
                      <Dropdown
                        value={franchise}
                        onChange={e => setFranchise(e.value)}
                        options={franchises.map(i => ({ code: i.id, name: i.name }))}
                        optionLabel="name"
                        placeholder="Selecione uma Franquia"
                      />
                    </div>
                  </div>
                ) : null }
              </div>
            </div>
          </>
        )
      )}
        {usersPending && usersPending.data.length > 0 ? (
          <div className="card">
            <h5>Aguardando aprovação:</h5>
            <div className="formgroup-inline" style={{alignItems: 'center'}}>
              {_renderUserPendings()}
            </div>
          </div>
        ) : null}
        <div className="card">
          <Toast ref={toast} />

          <DataTable
            ref={dt}
            value={records}
            dataKey="id"
            paginator
            stripedRows={true}
            rowHover
            showGridlines
            rows={10}
            rowsPerPageOptions={[5, 10, 25]}
            className="datatable-responsive"
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} registros"
            globalFilter={globalFilter}
            emptyMessage="Nenhum registro encontrado"
            header={header}
            responsiveLayout="scroll">
            <Column field="id" header="ID" sortable body={codeBodyTemplate} headerStyle={{}} />
            <Column field="name" header="Nome" sortable body={nameBodyTemplate} headerStyle={{}} />
            <Column field="phone" header="Telefone" sortable body={phoneBodyTemplate} headerStyle={{}} />
            <Column field="evaluation" header="Avaliação" sortable body={evaluationBodyTemplate} headerStyle={{}} />
            { !franchise_id ? (
              <Column field="franchise.name" header="Franquia" sortable body={franchiseBodyTemplate} headerStyle={{}} />
            ) : null }

            <Column header="Ações" className="flex flex-row align-items-center justify-content-end" body={actionBodyTemplate} />
          </DataTable>

          {/* Modal adicionar editar */}
          <Dialog
            visible={recordDialog}
            style={{ width: "550px" }}
            header={record?.id ? "Detalhes do Cliente" : "Novo Cliente"}
            modal
            className="p-fluid"
            footer={recordDialogFooter}
            onHide={hideDialog}>
            <div className="flex flex-column flex-wrap">
              <div className="field col-12 md:col-12">
                <label htmlFor="name">* Nome</label>
                <InputText
                  id="name"
                  value={record?.name}
                  onChange={e => onInputChange(e, "name")}
                  required
                  autoFocus
                  className={classNames("p-3", { "p-invalid": submitted && !record?.name })}
                />
                {submitted && !record?.name && <small className="p-invalid">Nome é obrigatório.</small>}
              </div>

              <div className="flex flex-column md:flex-row">
                <img
                  src={record?.image_path}
                  alt={record?.name}
                  width="150"
                  style={{ objectFit: "contain" }}
                  className="mt-0 mx-auto mb-5 block shadow-2"
                />
              </div>

              <div className="flex flex-column md:flex-row">
                <label htmlFor="name">CPF: {record?.cpf}</label>
              </div>

              { !franchise_id ? (
                <div className="flex flex-column md:flex-row flex-wrap">
                  <div className="field col-12 md:col-12">
                    <label htmlFor="name">Franquia</label>
                    <Dropdown
                      value={franchise}
                      onChange={e => setFranchise(e.value)}
                      options={franchises.map(i => ({ code: i.id, name: i.name }))}
                      optionLabel="name"
                      placeholder="Selecione uma franquia"
                    />
                  </div>
                </div>
              ) : null }
            </div>
          </Dialog>

          {/* Modal remover registro unico */}
          <Dialog
            visible={deleteRecordDialog}
            // style={{ width: "450px" }}
            header="Atenção"
            modal
            footer={deleteRecordDialogFooter}
            onHide={hideDeleteRecordDialog}>
            <div className="flex align-items-center justify-content-center">
              <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: "2rem" }} />
              {record && (
                <span>
                  Tem certeza de que deseja excluir <b>{record?.name}</b>?
                </span>
              )}
            </div>
          </Dialog>

          {/* Modal Aprovar usuário */}
          <Dialog
            visible={aproveUserDialog}
            style={{ width: "550px" }}
            header={"Aprovar usuário"}
            modal
            className="p-fluid"
            footer={aproveUserDialogFooter}
            onHide={hideDialog}>
            <div className="flex flex-column md:flex-row flex-wrap">
              <div className="flex flex-column md:flex-row flex-wrap">
                <div className="field col-12 md:col-12">
                  <label htmlFor="name">Selecione a Região</label>
                  <Dropdown
                    value={region}
                    onChange={e => setRegion(e.value)}
                    options={regions.map(i => ({ code: i.id, name: i.name }))}
                    optionLabel="name"
                    placeholder="Selecione uma Região"
                  />
                </div>
              </div>
            </div>
          </Dialog>
        </div>
      </div>
    </div>
  );
};

export default Crud;
